import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiCheckCircle,
  FiCreditCard,
  FiMessageSquare,
  FiRefreshCw,
  FiTruck,
} from "react-icons/fi";
import { toast } from "react-toastify";

import {
  addServiceOrderMessage,
  checkServiceOrderPaymentStatus,
  createServiceOrderPayment,
  getServiceOrder,
  respondToServiceOrderAdjustment,
} from "../../actions/serviceActions";
import { getPaymentConfig } from "../../actions/paymentActions";
import { PAYMENT_PROVIDERS } from "../../constants/paymentConstants";
import Loader from "../../components/Loader";
import Message from "../../components/Message";
import { getSafeUserMessage } from "../../utils/errorMessages";
import {
  formatServiceMethod,
  getQuantitySummary,
  getServiceDisplayName,
  getServiceOptionDisplayName,
  getServiceStatusLabel,
} from "../../utils/serviceCustomerCopy";
import "./ServicesScreen.css";

function safeText(value = "") {
  return String(value ?? "").trim();
}

function formatMoney(value = 0, currency = "GBP") {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: safeText(currency) || "GBP",
    }).format(Number(value || 0));
  } catch {
    return `£${Number(value || 0).toFixed(2)}`;
  }
}

function formatDate(value = "") {
  if (!value) return "Not set";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return "Not set";
  }
}

function getCheckoutUrl(payload = {}) {
  return (
    payload.checkoutUrl ||
    payload.hostedCheckoutUrl ||
    payload.approvalUrl ||
    payload.approveUrl ||
    payload.payerActionUrl ||
    payload.nextAction?.url ||
    ""
  );
}

function PaymentButton({ option, active, disabled, onClick }) {
  return (
    <button
      type="button"
      className={`service-pay-option${active ? " service-pay-option--active" : ""}`}
      disabled={disabled}
      onClick={onClick}
    >
      <FiCreditCard aria-hidden="true" />
      <span>
        <strong>{option.label}</strong>
        <small>{option.meta}</small>
      </span>
    </button>
  );
}

export default function ServiceOrderTrackerScreen() {
  const { reference = "" } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const detailsState = useSelector((state) => state.serviceOrderDetails || {});
  const paymentState = useSelector((state) => state.serviceOrderPayment || {});
  const messageState = useSelector((state) => state.serviceOrderMessage || {});
  const adjustmentState = useSelector(
    (state) => state.serviceOrderAdjustmentResponse || {},
  );
  const paymentConfig = useSelector((state) => state.payment?.config || {});

  const [selectedProvider, setSelectedProvider] = useState(PAYMENT_PROVIDERS.STRIPE);
  const [message, setMessage] = useState("");

  const serviceOrder =
    paymentState.serviceOrder || detailsState.serviceOrder || null;
  const serviceSnapshot = serviceOrder?.serviceSnapshot || {};
  const serviceDisplayName = getServiceDisplayName(serviceSnapshot);
  const optionDisplayName = getServiceOptionDisplayName(
    {
      label: serviceSnapshot.optionLabel,
      optionLabel: serviceSnapshot.optionLabel,
      unitLabel: serviceSnapshot.unitLabel,
    },
    serviceSnapshot,
  );
  const quantitySummary = getQuantitySummary(
    serviceSnapshot.quantity,
    serviceSnapshot,
    {
      label: serviceSnapshot.optionLabel,
      optionLabel: serviceSnapshot.optionLabel,
      unitLabel: serviceSnapshot.unitLabel,
    },
  );
  const returnedFromProvider = Boolean(searchParams.get("payment"));
  const providerFromReturn = safeText(searchParams.get("provider"));

  useEffect(() => {
    dispatch(getServiceOrder(reference)).catch((error) => {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not load this service order right now. Please try again later.",
        ),
      );
    });
    dispatch(getPaymentConfig()).catch(() => {});
  }, [dispatch, reference]);

  useEffect(() => {
    if (!returnedFromProvider || !reference) return;

    dispatch(
      checkServiceOrderPaymentStatus(reference, {
        provider: providerFromReturn || serviceOrder?.payment?.provider || "",
      }),
    )
      .then((result) => {
        if (result?.serviceOrder?.isPaid) {
          toast.success("Payment confirmed.");
        }
      })
      .catch((error) => {
        toast.error(
          getSafeUserMessage(
            error?.message,
            "We could not verify payment status right now. Please try again later.",
          ),
        );
      });
  }, [
    dispatch,
    providerFromReturn,
    reference,
    returnedFromProvider,
    serviceOrder?.payment?.provider,
  ]);

  useEffect(() => {
    if (!serviceOrder || serviceOrder.status !== "payment_processing") {
      return undefined;
    }

    const timer = setInterval(() => {
      dispatch(
        checkServiceOrderPaymentStatus(reference, {
          provider: serviceOrder.payment?.provider || selectedProvider,
        }),
      ).catch(() => {});
    }, 8000);

    return () => clearInterval(timer);
  }, [
    dispatch,
    reference,
    selectedProvider,
    serviceOrder,
    serviceOrder?.payment?.provider,
    serviceOrder?.status,
  ]);

  useEffect(() => {
    if (paymentConfig.sumupEnabled) {
      setSelectedProvider(PAYMENT_PROVIDERS.SUMUP);
    } else if (paymentConfig.paypalEnabled) {
      setSelectedProvider(PAYMENT_PROVIDERS.PAYPAL);
    } else if (paymentConfig.stripeEnabled) {
      setSelectedProvider(PAYMENT_PROVIDERS.STRIPE);
    }
  }, [
    paymentConfig.paypalEnabled,
    paymentConfig.stripeEnabled,
    paymentConfig.sumupEnabled,
  ]);

  const paymentOptions = useMemo(
    () =>
      [
        paymentConfig.sumupEnabled
          ? {
              provider: PAYMENT_PROVIDERS.SUMUP,
              label: "SumUp",
              meta: "Secure payment page",
            }
          : null,
        paymentConfig.paypalEnabled
          ? {
              provider: PAYMENT_PROVIDERS.PAYPAL,
              label: "PayPal",
              meta: "Secure checkout",
            }
          : null,
        paymentConfig.stripeEnabled
          ? {
              provider: PAYMENT_PROVIDERS.STRIPE,
              label: "Card",
              meta: "Secure card checkout",
            }
          : null,
      ].filter(Boolean),
    [
      paymentConfig.paypalEnabled,
      paymentConfig.stripeEnabled,
      paymentConfig.sumupEnabled,
    ],
  );

  const pendingAdjustments = (serviceOrder?.adjustments || []).filter(
    (adjustment) => adjustment.status === "pending",
  );
  const selectedProviderAvailable = paymentOptions.some(
    (option) => option.provider === selectedProvider,
  );
  const busy =
    detailsState.loading ||
    paymentState.loading ||
    messageState.loading ||
    adjustmentState.loading;

  const handleRefresh = () => {
    dispatch(
      checkServiceOrderPaymentStatus(reference, {
        provider: serviceOrder?.payment?.provider || selectedProvider,
      }),
    ).catch((error) => {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not refresh this service order right now. Please try again later.",
        ),
      );
    });
  };

  const handlePay = async () => {
    try {
      const result = await dispatch(
        createServiceOrderPayment(reference, {
          provider: selectedProvider,
        }),
      );
      const checkoutUrl = getCheckoutUrl(result);

      if (!checkoutUrl) {
        throw new Error("The payment provider did not return a checkout URL.");
      }

      window.location.assign(checkoutUrl);
    } catch (error) {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not start payment right now. Please try again later.",
        ),
      );
    }
  };

  const handleSendMessage = (event) => {
    event.preventDefault();
    if (!message.trim()) return;

    dispatch(addServiceOrderMessage(reference, message))
      .then(() => setMessage(""))
      .catch((error) => {
        toast.error(
          getSafeUserMessage(
            error?.message,
            "We could not send your message right now. Please try again later.",
          ),
        );
      });
  };

  const handleAdjustment = (adjustmentId, decision) => {
    dispatch(respondToServiceOrderAdjustment(reference, adjustmentId, decision))
      .then(() => toast.success("Adjustment response saved."))
      .catch((error) => {
        toast.error(
          getSafeUserMessage(
            error?.message,
            "We could not update that adjustment right now. Please try again later.",
          ),
        );
      });
  };

  return (
    <main className="services-page service-tracker">
      <section className="service-tracker__wrap">
        <div className="service-tracker__nav">
          <Link to="/services" className="btn btn-outline-dark">
            Services
          </Link>
          <button
            type="button"
            className="btn btn-dark"
            onClick={handleRefresh}
            disabled={busy}
          >
            <FiRefreshCw aria-hidden="true" /> Refresh
          </button>
        </div>

        {detailsState.loading && !serviceOrder ? (
          <div className="services-state">
            <Loader />
          </div>
        ) : detailsState.error ? (
          <Message variant="danger">{detailsState.error}</Message>
        ) : serviceOrder ? (
          <>
            <header className="service-tracker__header">
              <div>
                <span>{serviceOrder.reference}</span>
                <h1>{serviceDisplayName}</h1>
                <p>{serviceOrder.message}</p>
              </div>
              <strong>{getServiceStatusLabel(serviceOrder.status)}</strong>
            </header>

            <div className="service-tracker__grid">
              <section className="service-tracker__panel">
                <h2>Summary</h2>
                <div className="service-facts">
                  <div>
                    <span>Your selection</span>
                    <strong>{optionDisplayName}</strong>
                  </div>
                  <div>
                    <span>Amount</span>
                    <strong>{quantitySummary}</strong>
                  </div>
                  <div>
                    <span>Your price</span>
                    <strong>
                      {formatMoney(
                        serviceOrder.pricing?.grandTotal,
                        serviceOrder.pricing?.currency,
                      )}
                    </strong>
                  </div>
                  <div>
                    <span>Order number</span>
                    <strong>{serviceOrder.linkedOrderNumber || "Pending"}</strong>
                  </div>
                </div>

                <div className="service-status-box">
                  {serviceOrder.isPaid ? (
                    <FiCheckCircle aria-hidden="true" />
                  ) : (
                    <FiCreditCard aria-hidden="true" />
                  )}
                  <span>
                    {serviceOrder.isPaid
                      ? `Paid ${formatDate(serviceOrder.paidAt)}`
                      : serviceOrder.payment?.failureMessage ||
                        "Payment is not complete yet."}
                  </span>
                </div>

                {serviceOrder.canPay ? (
                  <div className="service-payment-panel">
                    <h3>Payment method</h3>
                    <div className="service-pay-options">
                      {paymentOptions.map((option) => (
                        <PaymentButton
                          key={option.provider}
                          option={option}
                          active={selectedProvider === option.provider}
                          disabled={busy}
                          onClick={() => setSelectedProvider(option.provider)}
                        />
                      ))}
                    </div>
                    {paymentState.error ? (
                      <p className="service-error">{paymentState.error}</p>
                    ) : null}
                    <button
                      type="button"
                      className="services-form__submit"
                      disabled={busy || !selectedProviderAvailable}
                      onClick={handlePay}
                    >
                      {paymentState.loading
                        ? "Opening secure payment..."
                        : "Continue to secure payment"}
                    </button>
                  </div>
                ) : null}
              </section>

              <section className="service-tracker__panel">
                <h2>Getting your garment to and from us</h2>
                <div className="service-facts">
                  <div>
                    <span>Getting your garment to us</span>
                    <strong>
                      {formatServiceMethod(serviceOrder.logistics?.inboundMethod)}
                    </strong>
                  </div>
                  <div>
                    <span>Getting your garment back</span>
                    <strong>
                      {formatServiceMethod(serviceOrder.logistics?.outboundMethod)}
                    </strong>
                  </div>
                  <div>
                    <span>Tracking to our shop</span>
                    <strong>
                      {serviceOrder.logistics?.inboundTrackingNumber || "Not added"}
                    </strong>
                  </div>
                  <div>
                    <span>Tracking back to you</span>
                    <strong>
                      {serviceOrder.logistics?.outboundTrackingUrl ? (
                        <a href={serviceOrder.logistics.outboundTrackingUrl}>
                          Track parcel
                        </a>
                      ) : (
                        serviceOrder.logistics?.outboundTrackingNumber ||
                        "Not added"
                      )}
                    </strong>
                  </div>
                </div>
                <div className="service-status-box">
                  <FiTruck aria-hidden="true" />
                  <span>
                    {serviceOrder.logistics?.notes ||
                      "Tracking details will appear here when available."}
                  </span>
                </div>
              </section>
            </div>

            {pendingAdjustments.length ? (
              <section className="service-tracker__panel">
                <h2>Adjustments</h2>
                <div className="service-adjustments">
                  {pendingAdjustments.map((adjustment) => (
                    <article key={adjustment.id}>
                      <div>
                        <strong>{adjustment.label}</strong>
                        <p>
                          {formatMoney(
                            adjustment.amount,
                            serviceOrder.pricing?.currency,
                          )}
                          {adjustment.reason ? ` - ${adjustment.reason}` : ""}
                        </p>
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-outline-dark btn-sm"
                          disabled={busy}
                          onClick={() => handleAdjustment(adjustment.id, "reject")}
                        >
                          Reject
                        </button>
                        <button
                          type="button"
                          className="btn btn-dark btn-sm"
                          disabled={busy}
                          onClick={() => handleAdjustment(adjustment.id, "accept")}
                        >
                          Accept
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            ) : null}

            <div className="service-tracker__grid">
              <section className="service-tracker__panel">
                <h2>Messages</h2>
                <div className="service-messages">
                  {(serviceOrder.messages || []).map((item) => (
                    <article key={item.id}>
                      <strong>{getServiceStatusLabel(item.authorType)}</strong>
                      <p>{item.message}</p>
                      <span>{formatDate(item.createdAt)}</span>
                    </article>
                  ))}
                  {!serviceOrder.messages?.length ? (
                    <p>No messages yet.</p>
                  ) : null}
                </div>
                <form className="service-message-form" onSubmit={handleSendMessage}>
                  <label className="services-form__field" htmlFor="service-message">
                    <span>
                      <FiMessageSquare aria-hidden="true" />
                      Message
                    </span>
                    <textarea
                      id="service-message"
                      rows="3"
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                    />
                  </label>
                  <button
                    type="submit"
                    className="services-form__submit"
                    disabled={messageState.loading || !message.trim()}
                  >
                    Send message
                  </button>
                </form>
              </section>

              <section className="service-tracker__panel">
                <h2>Timeline</h2>
                <div className="service-timeline">
                  {(serviceOrder.timeline || []).map((item) => (
                    <article key={item.id || `${item.type}-${item.createdAt}`}>
                      <strong>
                        {item.label || getServiceStatusLabel(item.type)}
                      </strong>
                      <p>{item.message}</p>
                      <span>{formatDate(item.createdAt)}</span>
                    </article>
                  ))}
                </div>
              </section>
            </div>
          </>
        ) : (
          <Message>We could not find this service order.</Message>
        )}
      </section>
    </main>
  );
}
